import React, {useContext} from 'react';

import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import {IconButton} from 'components/icon-button';
import {ModalContext} from 'components/modals/modal-context';

import * as ModalConstants from '../modal-constants';

import type {Theme as DefaultTheme} from '@material-ui/core/styles/createTheme';
import type {Props as DialogModalProps} from 'components/modals/sc-modal';
import type {PositionProperty} from 'csstype';

const getMarginValue = (size: DialogModalProps['size']) => {
    if (size === 'small') {
        return ModalConstants.modalMarginSm;
    }

    if (size === 'large') {
        return ModalConstants.modalMarginLg;
    }

    return ModalConstants.modalMarginMd;
};

const useStyles = makeStyles<
    DefaultTheme,
    {
        size: DialogModalProps['size'];
    }
>((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        '& > button': {
            backgroundColor: theme.palette.common.white,
        },
    },
    closeButton: ({size}) => {
        const margin = getMarginValue(size);
        return {
            position: 'absolute' as PositionProperty,
            top: `calc(${margin} - 0.4rem)`,
            right: margin,
            background: theme.palette.background.paper,
            zIndex: 1,
        };
    },
    title: ({size}) => {
        const margin = getMarginValue(size);
        return {
            ...theme.typography.componentM,
            fontWeight: theme.typography.fontWeightBold as number,
            color: theme.palette.text.secondary,
            margin: margin,
            marginBottom: ModalConstants.modalMarginSm,
        };
    },
}));

export type Props = {
    title: string;
    disableClose?: boolean | undefined;
    topImage?: React.ReactNode | undefined;
};

const ModalTitle = (props: Props) => {
    const {title, disableClose, topImage} = props;

    const {size, open, onClose} = useContext(ModalContext);
    const classes = useStyles({
        size,
    });

    return (
        <DialogTitle
            disableTypography
            classes={{
                root: classes.root,
            }}>
            {topImage && (
                <div
                    style={{
                        width: '100%',
                    }}>
                    {topImage}
                </div>
            )}
            <Typography className={classes.title}>{title}</Typography>
            {!disableClose && (
                <IconButton
                    variant='tertiary'
                    size='small'
                    className={classes.closeButton}
                    disabled={!open}
                    aria-label='close'
                    onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            )}
        </DialogTitle>
    );
};

export default ModalTitle;
